import { z } from 'zod';
/**
 * Distinguishes `of: ScalarField` from `of: ContentRecordShape`.
 *
 * A scalar declaration has a STRING `type` discriminant; a record shape maps
 * names to objects, so its `type` key (if an author ever names a field `type`)
 * holds an object. There is no ambiguity to resolve at runtime.
 */
export function isRecordShape(of) {
    return typeof of.type !== 'string';
}
// ── Zod mirrors ──────────────────────────────────────────────────────
const contentTextFieldSchema = z.object({
    type: z.literal('text'),
    description: z.string().min(1),
    maxChars: z.number().int().positive().optional(),
    optional: z.literal(true).optional(),
});
const contentNumberFieldSchema = z.object({
    type: z.literal('number'),
    description: z.string().min(1),
    min: z.number().optional(),
    max: z.number().optional(),
    optional: z.literal(true).optional(),
});
const contentChoiceFieldSchema = z.object({
    type: z.literal('choice'),
    description: z.string().min(1),
    options: z.array(z.string().min(1)).min(2),
    optional: z.literal(true).optional(),
});
export const contentScalarFieldSchema = z.discriminatedUnion('type', [
    contentTextFieldSchema,
    contentNumberFieldSchema,
    contentChoiceFieldSchema,
]);
export const contentRecordShapeSchema = z
    .record(z.string().min(1), contentScalarFieldSchema)
    .refine((shape) => Object.keys(shape).length > 0, {
    message: 'a record row must declare at least one field',
});
export const contentListFieldSchema = z.object({
    type: z.literal('list'),
    description: z.string().min(1),
    of: z.union([contentScalarFieldSchema, contentRecordShapeSchema]),
    min: z.number().int().min(0).optional(),
    max: z.number().int().min(1).optional(),
    optional: z.literal(true).optional(),
});
export const contentFieldSchema = z.union([
    contentScalarFieldSchema,
    contentListFieldSchema,
]);
export const contentSchemaSchema = z.record(z.string().min(1), contentFieldSchema);
/** A row value the compiler may bind (`{source:'field'}` reads from this). */
export const contentRowSchema = z.record(z.string().min(1), z.union([z.string(), z.number()]));
// ── Declaration → response schema ────────────────────────────────────
//
// The generated equivalent of the fifteen zod schemas v1 hand-wrote, one per
// copy shape. Everything above describes what a template DECLARES; this turns
// a declaration into the schema the model's RESPONSE is checked against.
function scalarResponseSchema(field) {
    if (field.type === 'text') {
        const base = z.string().min(1);
        return field.maxChars === undefined ? base : base.max(field.maxChars);
    }
    if (field.type === 'number') {
        let base = z.number();
        if (field.min !== undefined)
            base = base.min(field.min);
        if (field.max !== undefined)
            base = base.max(field.max);
        return base;
    }
    // `choice` — z.enum needs a non-empty tuple, which contentChoiceFieldSchema
    // guarantees by requiring at least two options.
    return z.enum(field.options);
}
function optionalise(schema, isOptional) {
    return isOptional ? schema.optional() : schema;
}
function fieldResponseSchema(field) {
    if (field.type !== 'list')
        return scalarResponseSchema(field);
    const item = isRecordShape(field.of)
        ? z.object(Object.fromEntries(Object.entries(field.of).map(([name, member]) => [
            name,
            optionalise(scalarResponseSchema(member), member.optional),
        ])))
        : scalarResponseSchema(field.of);
    let list = z.array(item);
    if (field.min !== undefined)
        list = list.min(field.min);
    if (field.max !== undefined)
        list = list.max(field.max);
    return list;
}
/**
 * The zod schema a model response must satisfy for `schema`.
 *
 * This is the STRICT reading — every declared bound rejects. The §8 failure
 * policy (truncate a list over `max`, drop a malformed optional, drop a bad
 * record row and fail only if the list then falls under `min`) layers on top in
 * a later step: it decides what to SALVAGE, and it needs the per-issue detail
 * this schema produces in order to decide.
 */
export function contentResponseSchema(schema) {
    return z.object(Object.fromEntries(Object.entries(schema).map(([name, field]) => [
        name,
        optionalise(fieldResponseSchema(field), field.optional),
    ])));
}
//# sourceMappingURL=content-schema.js.map